<template>
    <div class="qr-scan-display">
        <!-- Loading State -->
        <div class="loading-container" v-if="loading">
            <div class="spinner-wrapper">
                <Spin size="large"></Spin>
            </div>
            <p class="loading-text">{{ $t("common.loading") }}</p>
        </div>

        <!-- Error State -->
        <div class="error-container" v-else-if="error">
            <div class="error-icon-wrapper">
                <Icon type="ios-alert" size="64" />
            </div>
            <h2 class="error-title">{{ $t("branch.invalidQRCode") }}</h2>
            <p class="error-message">{{ errorMessage }}</p>
            <Button type="primary" size="large" @click="$router.push('/')">
                <Icon type="ios-home" />
                {{ $t("branch.backToHome") }}
            </Button>
        </div>

        <!-- Main Content -->
        <div class="content-container" v-else-if="branchData">
            <!-- Header with Company Info -->
            <div class="company-header">
                <div class="company-content">
                    <div class="logo-wrapper">
                        <img
                            v-if="branchData.company && branchData.company.company_logo"
                            :src="branchData.company.company_logo"
                            alt="Company Logo"
                            class="company-logo"
                        />
                        <div v-else class="no-logo">
                            <Icon type="ios-business" size="48" />
                        </div>
                    </div>
                    <div class="company-info">
                        <h1 class="company-name-en">
                            {{ branchData.company ? branchData.company.company_name_en : "" }}
                        </h1>
                        <h2 class="company-name-kh">
                            {{ branchData.company ? branchData.company.company_name_kh : "" }}
                        </h2>
                    </div>
                </div>
            </div>

            <!-- Login Section -->
            <div class="login-section">
                <div class="login-card">
                    <div class="welcome-text">
                        <Icon type="ios-lock" size="24" class="lock-icon" />
                        <h3>សូមស្វាគមន៍ទៅកាន់ប្រព័ន្ធ</h3>
                    </div>
                    
                    <form class="login-form" @submit.prevent="handleLogin">
                        <div class="form-group">
                            <label for="username" class="form-label">
                                <Icon type="ios-person" size="18" />
                                អ្នកប្រើប្រាស់
                            </label>
                            <input
                                type="text"
                                class="form-input"
                                placeholder="បញ្ចូលឈ្មោះអ្នកប្រើប្រាស់"
                                id="username"
                                v-model="username"
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="password" class="form-label">
                                <Icon type="ios-key" size="18" />
                                ពាក្យសម្ងាត់
                            </label>
                            <div class="password-wrapper">
                                <input
                                    :type="showPassword ? 'text' : 'password'"
                                    class="form-input"
                                    placeholder="បញ្ចូលពាក្យសម្ងាត់របស់អ្នក"
                                    id="password"
                                    v-model="password"
                                />
                                <button 
                                    type="button" 
                                    class="toggle-password"
                                    @click="showPassword = !showPassword"
                                >
                                    <Icon :type="showPassword ? 'ios-eye-off' : 'ios-eye'" size="20" />
                                </button>
                            </div>
                        </div>
                        
                        <div class="form-options">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="rememberMe"
                                    id="rememberMe"
                                />
                                <span class="checkbox-custom"></span>
                                <span class="checkbox-text">ចងចាំខ្ញុំ</span>
                            </label>
                            <a href="#" class="forgot-link">ភ្លេចពាក្យសម្ងាត់?</a>
                        </div>
                        
                        <button type="submit" class="btn-login">
                            <Icon type="ios-log-in" size="20" />
                            ចូលគណនី
                        </button>
                    </form>
                </div>
            </div>

            <!-- Certificate Section -->
            <div class="certificate-section">
                <div class="section-header">
                    <Icon type="ios-document-outline" size="28" />
                    <h3>វិញ្ញាបនបត្រ</h3>
                </div>
                <div class="certificate-container">
                    <div 
                        v-if="branchData.branch_certificate_photo"
                        class="certificate-wrapper"
                        @click="showFullImage"
                    >
                        <img
                            :src="branchData.branch_certificate_photo"
                            alt="Branch Certificate"
                            class="certificate-image"
                        />
                        <div class="image-overlay">
                            <Icon type="ios-expand" size="32" />
                            <span>ចុចដើម្បីមើលទំហំពេញ</span>
                        </div>
                    </div>
                    <div v-else class="no-certificate">
                        <Icon type="ios-image-outline" size="80" />
                        <p>{{ $t("branch.noCertificate") }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Full Image Modal -->
        <Modal
            v-model="showImageModal"
            :footer-hide="true"
            width="90%"
            class-name="full-image-modal"
        >
            <div class="modal-content">
                <img
                    v-if="branchData && branchData.branch_certificate_photo"
                    :src="branchData.branch_certificate_photo"
                    alt="Branch Certificate"
                    class="modal-image"
                />
            </div>
        </Modal>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import AppOptions from "@/config/AppOptions.vue";

export default {
    name: "QRCodeScanDisplay",
    data() {
        return {
            loading: true,
            error: false,
            errorMessage: "",
            branchData: null,
            showImageModal: false,
            currentDateTime: "",
            username: "",
            password: "",
            rememberMe: false,
            showPassword: false
        };
    },
    mounted() {
        this.currentDateTime = moment().format("DD-MM-YYYY HH:mm:ss");
        AppOptions.appEmpty = true;
        this.fetchBranchData();
    },
    methods: {
        async fetchBranchData() {
            try {
                const token = this.$route.query.token;                

                if (!token) {
                    this.error = true;
                    this.errorMessage = this.$t("branch.tokenRequired");
                    this.loading = false;
                    return;
                }

                const response = await axios.post(
                    `${process.env.VUE_APP_PROXY}/api/branches/decode-qr-token`,
                    { token }
                );

                if (response.data.success) {
                    const branchId = response.data.data.branch_id;

                    const branchResponse = await axios.get(
                        `${process.env.VUE_APP_PROXY}/api/branches/${branchId}/qr-display`
                    );

                    if (branchResponse.data.success) {
                        this.branchData = branchResponse.data.data;
                    } else {
                        this.error = true;
                        this.errorMessage = this.$t("branch.branchNotFound");
                    }
                } else {
                    this.error = true;
                    this.errorMessage = this.$t("branch.invalidToken");
                }
            } catch (error) {
                this.error = true;
                this.errorMessage =
                    error.response?.data?.message ||
                    this.$t("branch.errorLoadingData");
                console.error("Error fetching branch data:", error);
            } finally {
                this.loading = false;
            }
        },
        showFullImage() {
            this.showImageModal = true;
        },
        handleLogin() {
            // Handle login logic here
            console.log("Login attempted", {
                username: this.username,
                password: this.password,
                rememberMe: this.rememberMe
            });
        }
    }
};
</script>

<style scoped lang="scss">
.qr-scan-display {
    min-height: 100vh;
    padding: 20px;

    // Loading State
    .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 80vh;
        
        .spinner-wrapper {
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            padding: 30px;
            border-radius: 20px;
            margin-bottom: 20px;
        }
        
        .loading-text {
            color: white;
            font-size: 18px;
            font-weight: 500;
        }
    }

    // Error State
    .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 80vh;
        text-align: center;
        
        .error-icon-wrapper {
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            padding: 30px;
            border-radius: 50%;
            margin-bottom: 20px;
            color: #ff4757;
        }

        .error-title {
            color: white;
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
        }

        .error-message {
            color: rgba(255, 255, 255, 0.9);
            font-size: 16px;
            margin-bottom: 30px;
            max-width: 400px;
        }
    }

    // Main Content
    .content-container {
        max-width: 900px;
        margin: 0 auto;
        background: white;
        border-radius: 24px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        overflow: hidden;
        animation: slideUp 0.5s ease-out;
    }

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    // Company Header
    .company-header {
        padding: 40px 30px;
        border-bottom: 1px solid #e9ecef;
        
        .company-content {
            display: flex;
            align-items: center;
            gap: 24px;
            max-width: 800px;
            margin: 0 auto;
        }
        
        .logo-wrapper {
            flex-shrink: 0;

            .company-logo {
                width: 150px;
                object-fit: cover;
                transition: transform 0.3s ease;
                
                &:hover {
                    transform: scale(1.05);
                }
            }

            .no-logo {
                width: 100px;
                height: 100px;
                background: #f8f9fa;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #667eea;
                border: 4px solid #e9ecef;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
        }

        .company-info {
            flex: 1;
            text-align: left;
            
            .company-name-en {
                color: #3b86d1;
                font-size: 28px;
                font-weight: 700;
                margin: 0 0 8px 0;
                letter-spacing: -0.5px;
            }

            .company-name-kh {
                color: #3b86d1;
                font-size: 30px;
                margin: 0;
                font-weight: 500;
            }
        }
    }

    // Login Section
    .login-section {
        padding: 40px 30px;
        background: #EEFDDE;

        .login-card {
            max-width: 500px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .welcome-text {
            text-align: center;
            margin-bottom: 32px;
            
            .lock-icon {
                color: #667eea;
                margin-bottom: 12px;
            }

            h3 {
                font-size: 24px;
                font-weight: 600;
                color: #2c3e50;
                margin: 0;
            }
        }

        .login-form {
            .form-group {
                margin-bottom: 24px;

                .form-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #495057;
                    font-size: 14px;
                    margin-bottom: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .form-input {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid #e9ecef;
                    border-radius: 10px;
                    font-size: 15px;
                    transition: all 0.3s ease;
                    background: #f8f9fa;

                    &:focus {
                        outline: none;
                        border-color: #667eea;
                        background: white;
                        box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
                    }

                    &::placeholder {
                        color: #adb5bd;
                    }
                }
                
                .password-wrapper {
                    position: relative;
                    
                    .toggle-password {
                        position: absolute;
                        right: 12px;
                        top: 50%;
                        transform: translateY(-50%);
                        background: none;
                        border: none;
                        color: #6c757d;
                        cursor: pointer;
                        padding: 4px;
                        display: flex;
                        align-items: center;
                        transition: color 0.3s ease;
                        
                        &:hover {
                            color: #667eea;
                        }
                    }
                }
            }

            .form-options {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 28px;

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    gap: 8px;

                    input[type="checkbox"] {
                        display: none;

                        &:checked + .checkbox-custom {
                            background: #667eea;
                            border-color: #667eea;

                            &::after {
                                opacity: 1;
                                transform: scale(1);
                            }
                        }
                    }

                    .checkbox-custom {
                        width: 20px;
                        height: 20px;
                        border: 2px solid #dee2e6;
                        border-radius: 5px;
                        position: relative;
                        transition: all 0.3s ease;

                        &::after {
                            content: "✓";
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%) scale(0);
                            color: white;
                            font-size: 12px;
                            font-weight: bold;
                            opacity: 0;
                            transition: all 0.3s ease;
                        }
                    }

                    .checkbox-text {
                        color: #495057;
                        font-size: 14px;
                        user-select: none;
                    }
                }

                .forgot-link {
                    color: #667eea;
                    text-decoration: none;
                    font-size: 14px;
                    font-weight: 500;
                    transition: color 0.3s ease;

                    &:hover {
                        color: #764ba2;
                        text-decoration: underline;
                    }
                }
            }

            .btn-login {
                width: 100%;
                padding: 16px;
                background: linear-gradient(135deg, #667eea 0%, #31e978 100%);
                color: white;
                border: none;
                border-radius: 10px;
                font-size: 16px;
                font-weight: 600;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                transition: all 0.3s ease;
                box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
                }

                &:active {
                    transform: translateY(0);
                }
            }
        }
    }

    // Certificate Section
    .certificate-section {
        padding: 40px 30px;

        .section-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 24px;
            color: #667eea;

            h3 {
                font-size: 22px;
                font-weight: 600;
                margin: 0;
            }
        }

        .certificate-container {
            .certificate-wrapper {
                position: relative;
                border-radius: 12px;
                overflow: hidden;
                cursor: pointer;
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                transition: transform 0.3s ease, box-shadow 0.3s ease;

                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);

                    .image-overlay {
                        opacity: 1;
                    }
                }

                .certificate-image {
                    width: 100%;
                    height: auto;
                    display: block;
                }

                .image-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(102, 126, 234, 0.9);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    color: white;
                    opacity: 0;
                    transition: opacity 0.3s ease;

                    span {
                        font-size: 16px;
                        font-weight: 500;
                    }
                }
            }

            .no-certificate {
                padding: 80px 20px;
                background: #f8f9fa;
                border-radius: 12px;
                text-align: center;
                color: #adb5bd;
                border: 2px dashed #dee2e6;

                p {
                    margin-top: 20px;
                    font-size: 16px;
                    font-weight: 500;
                }
            }
        }
    }

    // Footer
    .footer {
        padding: 30px;
        background: #f8f9fa;
        text-align: center;
        border-top: 1px solid #e9ecef;

        .footer-info {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            margin-bottom: 20px;
            color: #6c757d;
            font-size: 14px;
        }
    }

    // Modal Styles
    .modal-content {
        .modal-image {
            width: 100%;
            height: auto;
            border-radius: 8px;
        }
    }

    // Responsive
    @media (max-width: 768px) {
        padding: 10px;

        .company-header {
            padding: 30px 20px;

            .company-content {
                flex-direction: column;
                text-align: center;
                gap: 16px;
            }

            .logo-wrapper {
                .company-logo,
                .no-logo {
                    width: 80px;
                    height: 80px;
                }
            }

            .company-info {
                text-align: center;
                
                .company-name-en {
                    font-size: 22px;
                }

                .company-name-kh {
                    font-size: 30px;
                }
            }
        }

        .login-section {
            padding: 30px 20px;

            .login-card {
                padding: 30px 20px;
            }

            .welcome-text h3 {
                font-size: 20px;
            }
        }

        .certificate-section {
            padding: 30px 20px;
        }

        .footer {
            padding: 20px;
            
            .footer-info {
                flex-direction: column;
                gap: 4px;
            }
        }
    }
}
</style>